import React, { useMemo } from "react";

import { StatCard, StatCardGroup } from "@/_self_managed/SM_Dashboard/comps/Stats";
import { DashboardStatsBarSkeleton } from "@/_self_managed/SM_Dashboard/comps/DashboardStatsBar";
import { useAggrMetricsAcrossDimensions } from "../../../WidgetBarChart/queries/useAggrMetricsAcrossDimensions";
import { ASSESSMENTS, DATASETS } from "../madSettings";
import { useRoster } from "./queries";

/**
 * The overview dashboard's stats bar, one card per assessment:
 *
 *   Baseline            the big number is the average assessment grade (the
 *   5.2                 "Final Learning grade level" the volunteer records,
 *   23 / 875 students   NOT the child's actual grade), over every child who
 *                       had that assessment; under it, how many children had
 *                       it, out of every child in MAD's feed.
 *
 * The numbers come from the aggregation endpoint every chart widget uses —
 * grouped by the assessment radio, a count and an average per group — so
 * the docs never leave the server. One doc per (child, assessment) makes
 * the count the number of children.
 *
 * Follows the report's and the widget's filters, like the Started table it
 * sits above. The total is the whole roster regardless — it is the
 * denominator "of all students", and the feed carries nothing a filter
 * could match.
 *
 * Life Skills records no assessment grade, so its cards carry the count
 * alone and the number reads "—".
 */
const AVERAGE = "averageLevel";

export const AssessmentStatsBar = ({ contentType, filters }) => {
  const dataset = DATASETS[contentType];
  const roster = useRoster(filters);
  const aggregation = useAggrMetricsAcrossDimensions({
    contentType,
    activeFilters: filters,
    dimensions: [dataset.assessmentValuePath.replace(/\.value$/, "")],
    metrics: [
      { type: "count", name: "count" },
      ...(dataset.levelValuePath ? [{ type: "avg", name: AVERAGE, field: dataset.levelValuePath }] : []),
    ],
  });

  const total = useMemo(() => new Set((roster.data || []).map((r) => String(r.value))).size, [roster.data]);
  // In the assessment cycle's order, with the assessments nobody has had yet
  // present as empty cards — the endpoint only returns groups that exist.
  const cards = useMemo(
    () =>
      ASSESSMENTS.map((a) => {
        const row = (aggregation.data || []).find((r) => String(r.value) === a.value);
        return { ...a, children: row?.count || 0, average: typeof row?.[AVERAGE] === "number" ? row[AVERAGE] : null };
      }),
    [aggregation.data]
  );

  if (roster.status !== "success" || aggregation.status !== "success") {
    return <DashboardStatsBarSkeleton cards={ASSESSMENTS.length} />;
  }

  return (
    <StatCardGroup>
      {cards.map((card) => (
        <StatCard
          key={card.value}
          title={card.display}
          value={card.average === null ? "—" : (Math.round(card.average * 10) / 10).toString()}
          subline={`${card.children} / ${total} students${total ? ` (${Math.round((card.children / total) * 100)}%)` : ""}`}
        />
      ))}
    </StatCardGroup>
  );
};
